import { Modal, Spin, Table } from "antd";
import axios from "axios";
import React from "react";
import { useQuery } from "react-query";
import { serverUrl } from "../nestserver";

const GROUPS = [
  { key: "mandatory_subjects", title: "Mandatory Subjects" },
  { key: "languages", title: "Language Subjects" },
  { key: "co_curricular", title: "Co-Curricular" },
];

// "12", not "12.0", but keeps the half in "11.5". Half days count as 0.5.
const days = (value) => (Number.isInteger(value) ? value : value?.toFixed(1));

const subjectColumns = [
  { title: "Subject", dataIndex: "name", key: "name" },
  {
    title: "Max",
    dataIndex: "max",
    key: "max",
    align: "center",
    width: 80,
    render: (value) => (value == null ? "" : value),
  },
  {
    title: "Marks",
    dataIndex: "marks",
    key: "marks",
    align: "center",
    width: 90,
    render: (value) => (value == null ? "-" : value),
  },
  {
    title: "Grade",
    dataIndex: "grade",
    key: "grade",
    align: "center",
    width: 90,
    render: (value) => value || "-",
  },
  { title: "Comments", dataIndex: "comment", key: "comment" },
];

const SummaryRow = ({ label, value }) =>
  value ? (
    <div className="flex py-1 border-b border-gray-100">
      <div className="w-40 font-semibold">{label}</div>
      <div className="flex-1">{value}</div>
    </div>
  ) : null;

const Summary = ({ card, totalLabel = "Total Marks" }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <SummaryRow label="Overall Grade" value={card.overall_grade} />
    <SummaryRow
      label={totalLabel}
      value={
        card.max_marks
          ? `${card.total_marks ?? 0} / ${card.max_marks}`
          : String(card.total_marks ?? 0)
      }
    />
    <SummaryRow
      label="Class Rank"
      value={card.rank ? `${card.rank} of ${card.out_of}` : ""}
    />
    <SummaryRow
      label="Attendance"
      value={
        card.attendance
          ? `Working days ${days(
              card.attendance.total_days,
            )} | Present ${days(card.attendance.present_days)} days | Absent ${days(
              card.attendance.absent_days,
            )} days | ${card.attendance.percent}%`
          : ""
      }
    />
    <SummaryRow label="Comments" value={card.overall_comment} />
  </div>
);

/**
 * A combined year-end card: one block per exam, laid out like that exam's own
 * progress card, then the year summary. Mirrors the downloaded PDF.
 */
export const CombinedProgressCardView = ({ card }) => (
  <div>
    {card.combination_name ? (
      <div className="text-xl font-bold pb-1">{card.combination_name}</div>
    ) : null}
    <div className="text-base pb-4">
      <span className="font-semibold capitalize">{card.name}</span>
      {card.unique_id ? (
        <span className="text-gray-500"> · {card.unique_id}</span>
      ) : null}
      {card.year ? <span className="text-gray-500"> · {card.year}</span> : null}
    </div>

    {(card.exams ?? []).map((exam) => (
      <div key={exam.template_id} className="pb-8">
        <div className="text-lg font-bold pb-3 capitalize">{exam.name}</div>
        {exam.unavailable ? (
          <div className="text-gray-500 italic pb-3">
            The detailed marks for this exam are no longer available.
          </div>
        ) : null}
        {GROUPS.map((group) =>
          (exam.groups?.[group.key] ?? []).length ? (
            <div key={group.key} className="pb-4">
              <div className="font-semibold pb-2">{group.title}</div>
              <Table
                size="small"
                rowKey={(row, index) => `${row.name}-${index}`}
                columns={subjectColumns}
                dataSource={exam.groups[group.key]}
                pagination={false}
              />
            </div>
          ) : null,
        )}
        <Summary card={exam} />
      </div>
    ))}

    <div className="text-lg font-bold pb-3">Year Summary</div>
    <Summary card={card} />
  </div>
);

/** Fetches one combined card and shows it in a modal. */
const CombinedProgressCardModal = ({ id, onClose }) => {
  const card = useQuery(
    ["progress-card-combined", id],
    async () => {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/${id}`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      return res.data?.result?.data;
    },
    { enabled: !!id },
  );

  return (
    <Modal
      open={!!id}
      onCancel={onClose}
      footer={null}
      width={900}
      title="Year-end Progress Card"
      destroyOnClose
    >
      {card.isLoading ? (
        <div className="text-center py-10">
          <Spin />
        </div>
      ) : card.data ? (
        <CombinedProgressCardView card={card.data} />
      ) : (
        <div className="py-6 text-center text-gray-500">
          This progress card could not be loaded.
        </div>
      )}
    </Modal>
  );
};

export default CombinedProgressCardModal;
